import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  inject,
  LOCALE_ID,
  OnInit,
} from '@angular/core'
import { DatePipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatSelectModule } from '@angular/material/select'
import { ActivatedRoute, RouterLink } from '@angular/router'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import {
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator'
import {
  PurchaseOrder,
  PurchaseOrderItem,
  PurchaseOrderService,
  PurchaseOrderStatus,
} from '../../services/purchase-order.service'
import { TranslationService } from '../../services/translation.service'
import { RouteService } from '../../services/route.service'
import { ROUTES } from '../../constants/routes'
import { REQUISITION_STATUS } from '../../constants/requisition'
import { PaginatorIntl } from '../intl/paginator-intl'

@Component({
  selector: 'app-purchase-orders',
  imports: [
    RouterLink,
    DatePipe,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
  ],
  templateUrl: './purchase-orders.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './purchase-orders.component.scss',
  providers: [{ provide: MatPaginatorIntl, useClass: PaginatorIntl }],
})
export class PurchaseOrdersComponent implements OnInit {
  private api = inject(PurchaseOrderService)
  private route = inject(ActivatedRoute)
  private routeService = inject(RouteService)
  private host = inject<ElementRef<HTMLElement>>(ElementRef)
  private locale = inject(LOCALE_ID)
  t = inject(TranslationService)
  id = this.route.snapshot.paramMap.get('id')
  orders: PurchaseOrder[] = []
  order: PurchaseOrder | null = null
  loading = true
  error = ''
  downloadError = ''
  downloading = false
  pageIndex = 0
  pageSize = 10
  statusFilter: PurchaseOrderStatus | 'all' = 'all'
  readonly statusFilters: PurchaseOrderStatus[] = [
    REQUISITION_STATUS.APPROVED,
    REQUISITION_STATUS.PARTIALLY_APPROVED,
  ]
  groups: { id: string; name: string; items: PurchaseOrderItem[] }[] = []

  /** Set navigation context and load the purchase-order view. */
  ngOnInit(): void {
    this.routeService.setRoute(
      this.routeService.getUserRoutes(
        ROUTES.PURCHASE_ORDERS.title,
        ROUTES.PURCHASE_ORDERS.path,
      ),
    )
    this.load()
  }

  /** Return filtered orders for the current page. */
  get visibleOrders(): PurchaseOrder[] {
    return this.filteredOrders.slice(
      this.pageIndex * this.pageSize,
      (this.pageIndex + 1) * this.pageSize,
    )
  }

  /** Return orders matching the selected status. */
  get filteredOrders(): PurchaseOrder[] {
    return this.statusFilter === 'all'
      ? this.orders
      : this.orders.filter((order) => order.status === this.statusFilter)
  }

  /** Return to the first page when the status filter changes. */
  filterChanged(): void {
    this.pageIndex = 0
  }

  /** Clear the status filter and return to the first page. */
  clearFilter(): void {
    this.statusFilter = 'all'
    this.filterChanged()
  }

  /** Return the icon for the saved approval status. */
  statusIcon(status: PurchaseOrderStatus): string {
    return status === REQUISITION_STATUS.APPROVED ? 'check_circle' : 'rule'
  }

  /** Count decimal places, including values written in scientific notation. */
  private decimalPlaces(amount: number): number {
    const [coefficient, exponent = '0'] = String(amount).split('e')
    return Math.max(
      0,
      (coefficient.split('.')[1]?.length || 0) - Number(exponent),
    )
  }

  /** Return the calculated amount precision, or undefined for an unpriced item. */
  lineAmountScale(item: PurchaseOrderItem): number | undefined {
    return item.priceAmount === null
      ? undefined
      : this.decimalPlaces(item.priceAmount) + this.decimalPlaces(item.quantity)
  }

  /** Return the greatest line amount precision for the given currency. */
  subtotalScale(currency: string): number | undefined {
    const scales = (this.order?.items || [])
      .filter((item) => item.currency === currency && item.lineAmount !== null)
      .flatMap((item) => {
        const scale = this.lineAmountScale(item)
        return scale === undefined ? [] : [scale]
      })
    return scales.length ? Math.max(...scales) : undefined
  }

  /**
   * Format an amount for the current locale, preserving saved decimal precision.
   * An optional scale removes arithmetic artifacts from calculated amounts.
   */
  formatAmount(amount: number, scale?: number): string {
    const value =
      scale !== undefined && scale <= 100 && this.decimalPlaces(amount) > scale
        ? Number(amount.toFixed(scale))
        : amount
    const decimals = this.decimalPlaces(value)
    // Keep very small amounts readable in scientific notation.
    if (decimals > 20) return String(value)
    // Format integer and fractional digits separately to avoid further rounding.
    const [coefficient, exponent = '0'] = String(Math.abs(value)).split('e')
    const [whole, fraction = ''] = coefficient.split('.')
    const digits = whole + fraction
    const decimalAt = whole.length + Number(exponent)
    const integerText =
      decimalAt <= 0 ? '0' : digits.slice(0, decimalAt).padEnd(decimalAt, '0')
    const fractionText =
      decimalAt < 0 ? '0'.repeat(-decimalAt) + digits : digits.slice(decimalAt)
    const integer = BigInt(integerText) * (value < 0 ? -1n : 1n)
    const grouped = new Intl.NumberFormat(this.locale, {
      maximumFractionDigits: 0,
    }).format(integer === 0n && value < 0 ? -0 : integer)
    const separator = new Intl.NumberFormat(this.locale)
      .formatToParts(1.1)
      .find((part) => part.type === 'decimal')!.value
    return grouped + separator + fractionText.padEnd(2, '0')
  }

  /** Return a compact display reference without changing the saved PO number. */
  displayNumber(order: PurchaseOrder): string {
    if (
      /^PO-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        order.number,
      ) &&
      /^REQ-(?:[A-Z0-9]{4}-){3}[A-Z0-9]{4}$/.test(order.requisitionReference)
    ) {
      return `PO-${order.requisitionReference.slice(4)}`
    }
    return order.number
  }

  /** Apply pagination changes and scroll to the page heading. */
  pageChanged(event: PageEvent): void {
    this.pageIndex = event.pageIndex
    this.pageSize = event.pageSize
    this.host.nativeElement
      .querySelector('h1')
      ?.scrollIntoView({ block: 'start' })
  }

  /** Load the order list, or a single order grouped by supplier. */
  load(): void {
    this.loading = true
    this.error = ''
    if (this.id) {
      this.api.get(this.id).subscribe({
        next: (order) => {
          this.order = order
          const groups = new Map<
            string,
            { id: string; name: string; items: PurchaseOrderItem[] }
          >()
          for (const item of order.items) {
            let group = groups.get(item.sellerCompanyId)
            if (!group) {
              group = {
                id: item.sellerCompanyId,
                name: item.supplierName,
                items: [],
              }
              groups.set(group.id, group)
            }
            group.items.push(item)
          }
          this.groups = [...groups.values()]
          this.loading = false
        },
        error: () => {
          this.error = this.t.getPurchaseOrderLoadFailedText()
          this.loading = false
        },
      })
    } else {
      this.api.list().subscribe({
        next: (orders) => {
          this.orders = orders
          this.loading = false
        },
        error: () => {
          this.error = this.t.getPurchaseOrderLoadFailedText()
          this.loading = false
        },
      })
    }
  }

  /** Return supplied procurement references with translated labels. */
  references(
    item: PurchaseOrderItem,
  ): { label: string; value: string | number }[] {
    const keys = [
      'BusinessUnitId',
      'storageLocation',
      'batchId',
      'companyCode',
      'purchaseOrder',
      'purchaseOrderItem',
      'movementType',
      'itemDocument',
      'itemDocumentItem',
      'transactionDate',
      'invoiceNumber',
      'invoiceAmount',
      'purchaseOrderID',
      'goodsReceiptReference',
    ] as const
    return keys.flatMap((key) =>
      item[key] != null && item[key] !== ''
        ? [
            {
              label: this.t.getProcurementReferenceText(key),
              value: item[key]!,
            },
          ]
        : [],
    )
  }

  /** Download the PDF using the saved PO number as its filename. */
  download(): void {
    if (!this.order || this.downloading) return
    const order = this.order
    this.downloading = true
    this.downloadError = ''
    this.api.download(order.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${order.number}.pdf`
        link.hidden = true
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 60000)
        this.downloading = false
      },
      error: () => {
        this.downloadError = this.t.getPurchaseOrderDownloadFailedText()
        this.downloading = false
        this.host.nativeElement
          .querySelector('h1')
          ?.scrollIntoView({ block: 'start' })
      },
    })
  }
}
